/**
 * Resilient token storage for Thqah.
 *
 * iOS Capacitor WebView occasionally wipes `localStorage` between sessions
 * (low-memory, OS update, long inactivity, etc.), which signs the user out
 * on every cold start. We mitigate this by writing the JWT to BOTH
 * localStorage AND a same-origin cookie. On read, we fall back to the cookie
 * when localStorage is empty and copy it back, restoring the session
 * transparently.
 *
 * Cookie is set with `Max-Age=63072000` (~2 years) + Secure + SameSite=Lax
 * so it survives WebView restarts and is sent on top-level navigations only.
 */

const KEY = "thqah_token";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 730; // ~2 years

function writeCookie(value) {
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${KEY}=${encodeURIComponent(value)}; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
  } catch {/* ignore */}
}

function readCookie() {
  try {
    const m = document.cookie.match(new RegExp("(?:^|; )" + KEY + "=([^;]*)"));
    return m ? decodeURIComponent(m[1]) : null;
  } catch { return null; }
}

function clearCookie() {
  try {
    document.cookie = `${KEY}=; Path=/; Max-Age=0; SameSite=Lax`;
  } catch {/* ignore */}
}

export function getToken() {
  try {
    const ls = localStorage.getItem(KEY);
    if (ls) return ls;
  } catch {/* ignore */}
  // localStorage was wiped — restore from cookie if we still have it.
  const ck = readCookie();
  if (ck) {
    try { localStorage.setItem(KEY, ck); } catch {/* ignore */}
    return ck;
  }
  return null;
}

export function setToken(token) {
  try { localStorage.setItem(KEY, token); } catch {/* ignore */}
  writeCookie(token);
}

export function clearToken() {
  try { localStorage.removeItem(KEY); } catch {/* ignore */}
  clearCookie();
}
